import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000 // matches the server-side cache
const RANGE_KEY = 'analytics_range'
const FILTERS_KEY = 'analytics_filters'

export const RANGES = [
  { value: '24_hours', label: '24h', long: 'Last 24 hours' },
  { value: '7_days',   label: '7d',  long: 'Last 7 days'   },
  { value: '2_weeks',  label: '14d', long: 'Last 14 days'  },
  { value: '1_month',  label: '30d', long: 'Last 30 days'  },
  { value: '3_months', label: '90d', long: 'Last 90 days'  },
  { value: '6_months', label: '6mo', long: 'Last 6 months' },
  { value: '1_year',   label: '1y',  long: 'Last 12 months' },
]

const DEFAULT_RANGE = '7_days'
const VALID = RANGES.map(r => r.value)

export const BOOKED_BY = ['attendee', 'host']
export const PAGE_TYPES = ['profile', 'event']

// The filter shape per tab. Lists are always arrays and scalars always strings,
// so nothing downstream has to guard for null before mapping.
export function defaultFilters(tab) {
  return tab === 'traffic'
    ? { channels: [], countries: [], devices: [], pageType: '' }
    : { eventIds: [], statuses: [], locations: [], bookedBy: '' }
}

// Restored filters were written by a previous session and are not trusted.
// Unlike the task stats filters there is no fixed list to check most of these
// against — an event id or a country name is the host's own data — so the
// shapes are enforced here and the values themselves are checked by the server,
// which intersects unknown ones away.
function sanitize(tab, raw) {
  const clean = defaultFilters(tab)
  if (!raw || typeof raw !== 'object') return clean

  const list = (v) => (Array.isArray(v) ? v.filter(x => typeof x === 'string' && x) : [])
  const pick = (v, allowed) => (allowed.includes(v) ? v : '')

  if (tab === 'traffic') {
    clean.channels = list(raw.channels)
    clean.countries = list(raw.countries)
    clean.devices = list(raw.devices)
    clean.pageType = pick(raw.pageType, PAGE_TYPES)
  } else {
    clean.eventIds = list(raw.eventIds)
    clean.statuses = list(raw.statuses)
    clean.locations = list(raw.locations)
    clean.bookedBy = pick(raw.bookedBy, BOOKED_BY)
  }
  return clean
}

// The range persists so returning to the page picks up where you left off. It
// is read back through the same whitelist the server applies, because what
// comes out of localStorage was written by a previous session and is not
// trusted input.
function storedRange() {
  try {
    const saved = localStorage.getItem(RANGE_KEY)
    return VALID.includes(saved) ? saved : DEFAULT_RANGE
  } catch {
    return DEFAULT_RANGE
  }
}

function storedFilters(tab) {
  try {
    return sanitize(tab, JSON.parse(localStorage.getItem(FILTERS_KEY))?.[tab])
  } catch {
    return defaultFilters(tab)
  }
}

function persistFilters(all) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(all))
  } catch {
    // Private mode or blocked site data. Remembering filters is a
    // convenience, never a reason to break the page.
  }
}

// Both tabs share this shape. Payloads are cached per tab *and* per query, so
// flicking between two filter sets you have already looked at is instant.
function emptyTab() {
  return { data: {}, fetchedAt: {}, loading: false, error: null, options: null }
}

// The query string doubles as the cache key: it is exactly the set of inputs
// that changes the answer. Lists are appended as `name[]` pairs because that is
// what Rails parses into an array; the shareable URL uses a comma separated
// form instead (see useAnalyticsUrlState).
export function buildQuery(tab, range, filters) {
  const params = new URLSearchParams()
  params.set('range', range)

  if (tab === 'traffic') {
    filters.channels.forEach(v => params.append('channel[]', v))
    filters.countries.forEach(v => params.append('country[]', v))
    filters.devices.forEach(v => params.append('device[]', v))
    if (filters.pageType) params.set('page_type', filters.pageType)
  } else {
    filters.eventIds.forEach(v => params.append('event_id[]', v))
    filters.statuses.forEach(v => params.append('status[]', v))
    filters.locations.forEach(v => params.append('location[]', v))
    if (filters.bookedBy) params.set('booked_by', filters.bookedBy)
  }
  return params.toString()
}

export const useAnalyticsStore = defineStore('analytics', {
  state: () => ({
    range: storedRange(),
    filters: {
      bookings: storedFilters('bookings'),
      traffic: storedFilters('traffic'),
    },
    bookings: emptyTab(),
    traffic: emptyTab(),
  }),

  getters: {
    queryFor: (state) => (tab) => buildQuery(tab, state.range, state.filters[tab]),

    // The payload for the range and filters currently selected, or null while
    // the first request for them is still open.
    statsFor: (state) => (tab) => state[tab].data[buildQuery(tab, state.range, state.filters[tab])] ?? null,
    bookingsStats() { return this.statsFor('bookings') },
    trafficStats() { return this.statsFor('traffic') },

    // Options survive a filter change: they come from the last payload that
    // carried them, so the dropdowns do not empty out while a request is open.
    optionsFor: (state) => (tab) => state[tab].options ?? {},

    hasActiveFilters: (state) => (tab) => {
      const f = state.filters[tab]
      return Object.values(f).some(v => (Array.isArray(v) ? v.length > 0 : !!v))
    },

    rangeLabel: (state) => RANGES.find(r => r.value === state.range)?.long ?? '',
  },

  actions: {
    setRange(value) {
      if (!VALID.includes(value)) return
      this.range = value
      try {
        localStorage.setItem(RANGE_KEY, value)
      } catch {
        // See storedRange.
      }
    },

    // Every filter change goes through here so the refetch can never be
    // forgotten at a call site.
    async setFilters(tab, patch) {
      Object.assign(this.filters[tab], patch)
      persistFilters(this.filters)
      await this.fetchIfStale(tab)
    },

    async resetFilters(tab) {
      this.filters[tab] = defaultFilters(tab)
      persistFilters(this.filters)
      await this.fetchIfStale(tab)
    },

    // The range as it arrived in a URL. Sets without persisting, for the same
    // reason hydrateFilters does not: opening a colleague's link must not
    // quietly replace your own remembered range with theirs.
    hydrateRange(value) {
      if (VALID.includes(value)) this.range = value
    },

    // Applies filters that came from the URL. Deliberately neither persists nor
    // fetches: it runs before the page mounts, so the component's own
    // `fetchIfStale` picks these up a moment later, and writing them to
    // localStorage would mean opening a colleague's link quietly replaced your
    // own saved filters with theirs.
    hydrateFilters(tab, patch) {
      if (!patch) return
      this.filters[tab] = sanitize(tab, { ...this.filters[tab], ...patch })
    },

    isFresh(tab, key) {
      const at = this[tab].fetchedAt[key]
      return at != null && Date.now() - at < CACHE_TTL
    },

    async fetchIfStale(tab) {
      const key = this.queryFor(tab)
      if (this.isFresh(tab, key) && this[tab].data[key]) return
      await this.fetch(tab, key)
    },

    async fetch(tab, key = this.queryFor(tab)) {
      const slice = this[tab]
      slice.loading = true
      slice.error = null

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', `/api/v1/analytics/${tab}?${key}`, {}, false)

      if (!res.js_error && res.response?.success) {
        slice.data[key] = res.response.data
        slice.fetchedAt[key] = Date.now()
        if (res.response.data.filter_options) slice.options = res.response.data.filter_options
      } else {
        // The fetch timestamp is deliberately not set on failure, so the next
        // mount retries rather than sitting on an error for five minutes.
        slice.error = 'We could not load your analytics. Please try again.'
      }
      slice.loading = false
    },

    invalidate(tab) {
      const tabs = tab ? [tab] : ['bookings', 'traffic']
      tabs.forEach((name) => { this[name].fetchedAt = {} })
    },

    async refresh(tab) {
      this.invalidate(tab)
      await this.fetch(tab)
    },
  },
})
